import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { identify as analyticsIdentify, reset as analyticsReset } from "@/lib/analytics";
import type { Session, User } from "@supabase/supabase-js";

export type OrgRole = "owner" | "admin" | "operator" | "viewer" | "superadmin";

interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: OrgRole;
}

interface AuthContextValue {
  user: AuthUser | null;
  session: Session | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function getFallbackAuthUser(user: User): AuthUser {
  return {
    id: user.id,
    name: user.user_metadata?.name || user.user_metadata?.full_name || "",
    email: user.email || "",
    role: "viewer",
  };
}

async function buildAuthUser(user: User): Promise<AuthUser> {
  const fallback = getFallbackAuthUser(user);
  // STAB-3: single RPC replaces the previous 3-query N+1 (profile + membership + role).
  const { data, error } = await supabase.rpc("get_current_user_context");
  if (error || !data || typeof data !== "object") {
    if (error) console.error("get_current_user_context failed, falling back:", error);
    return fallback;
  }
  const ctx = data as {
    authenticated?: boolean;
    profile_name?: string;
    role?: string;
  };
  if (!ctx.authenticated) return fallback;
  return {
    ...fallback,
    name: ctx.profile_name?.trim() || fallback.name,
    role: (ctx.role as OrgRole) ?? "viewer",
  };
}

export const AuthProvider = React.forwardRef<unknown, { children: ReactNode }>(function AuthProvider({ children }, _ref) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const applySession = (nextSession: Session | null) => {
      if (!isMounted) return;
      setSession(nextSession);
      setLoading(false);

      if (!nextSession?.user) {
        setUser(null);
        return;
      }

      void buildAuthUser(nextSession.user)
        .then((nextUser) => {
          if (!isMounted) return;
          setUser(nextUser);
          analyticsIdentify(nextUser.id, nextUser.email);
        })
        .catch((error) => {
          console.error("Failed to build auth user:", error);
          if (!isMounted) return;
          setUser(getFallbackAuthUser(nextSession.user));
        });
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      applySession(nextSession);
    });

    supabase.auth
      .getSession()
      .then(({ data: { session: nextSession } }) => {
        applySession(nextSession);
      })
      .catch((error) => {
        console.error("Failed to get session:", error);
        if (!isMounted) return;
        setSession(null);
        setUser(null);
        setLoading(false);
      });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw new Error(error.message);
  }, []);

  const signup = useCallback(async (email: string, password: string, name: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name } },
    });
    if (error) throw new Error(error.message);
  }, []);

  const loginWithGoogle = useCallback(async () => {
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: `${window.location.origin}/`,
    });
    if (result && "error" in result && result.error) {
      throw new Error(String(result.error));
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw new Error(error.message);
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw new Error(error.message);
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    analyticsReset();
  }, []);

  return (
    <AuthContext.Provider value={{
      user, session, isAuthenticated: !!session, loading,
      login, signup, loginWithGoogle, resetPassword, updatePassword, logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
});

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
